/**
 * Auth Component — Login / New-Password / Reset-Password flows
 * Sub-components extracted into auth/ directory.
 */

import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { checkPassword, describePasswordPolicy } from "../../config/passwordPolicy";
import { getSignupUrl } from "../../config/runtime";
import { useCognito } from "../../contexts/CognitoProvider";
import { useConfig } from "../../contexts/ConfigContext";
import { useWebSocket } from "../../contexts/WebSocketProvider";
import {
    ChallengeRequired,
    clearTokens,
    confirmPasswordReset,
    initiatePasswordReset
} from "../../services/authService";
import { getToken } from "../../utils/tokenStorage";
import "../Auth.css";
import {
    RESET_CODE_REQUESTED_MESSAGE,
    messageForAuthError,
    revealsAccountExistence,
} from "./authMessages";

import LoginForm from "./LoginForm";
import NewPasswordForm from "./NewPasswordForm";
import ResetPasswordForm from "./ResetPasswordForm";

const Auth: React.FC = () => {
  const navigate = useNavigate();
  const config = useConfig();
  const {
    isAuthenticated,
    login,
    completeNewPassword,
    isLoading: isAuthLoading,
    updateUserFromAuthResponse,
  } = useCognito();
  const { connect: connectWebSocket } = useWebSocket();

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [error, setError] = useState("");
  const [showResetForm, setShowResetForm] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [resetCode, setResetCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [resetStep, setResetStep] = useState<"email" | "code" | "reset">(
    "email",
  );
  const [resetMessage, setResetMessage] = useState("");
  // AL-472: the tone is carried explicitly rather than inferred from the text.
  // ResetPasswordForm used to test the message for the substring "successful"
  // to pick its CSS class, which made styling a second channel that could
  // disagree with the words — and rendered "Verification code sent" in the red
  // error style, because that string does not contain "successful".
  const [resetMessageTone, setResetMessageTone] = useState<
    "info" | "success" | "error"
  >("info");
  const [showNewPasswordForm, setShowNewPasswordForm] = useState(false);
  const [pendingNewPassword, setPendingNewPassword] = useState("");
  const [pendingConfirmPassword, setPendingConfirmPassword] = useState("");
  const [challengeSession, setChallengeSession] = useState("");
  const [challengeUsername, setChallengeUsername] = useState("");
  // AL-470: the temporary password is deliberately not retained. The Cognito
  // challenge session already represents the accepted first factor.

  // Redirect to dashboard once authenticated. AL-470: this now also covers the
  // first-login path, because completeNewPassword commits the session to the
  // provider — so no explicit navigate() after setting a password.
  useEffect(() => {
    if (isAuthenticated) {
      navigate("/portal/dashboard", { replace: true });
    }
  }, [isAuthenticated, navigate]);

  // AL-470: one policy check, derived from config, for every password field.
  const newPasswordCheck = checkPassword(pendingNewPassword, config.auth);
  const resetPasswordCheck = checkPassword(newPassword, config.auth);

  const validatePasswordRules = (pwd: string) =>
    checkPassword(pwd, config.auth).isValid;

  /**
   * AL-470 typed the failures; AL-472 moved the wording into authMessages.ts so
   * that "every credential failure reads the same" is one testable assertion
   * rather than a convention spread across this switch.
   */
  const messageForError = (
    err: unknown,
    context: "signIn" | "newPassword" | "resetRequest" | "resetConfirm",
  ) => messageForAuthError(err, context, config.auth);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setIsLoggingIn(true);

    try {
      const result = await login({ username, password });

      if ("challengeName" in result) {
        const challengeResult = result as ChallengeRequired;
        if (challengeResult.challengeName === "NEW_PASSWORD_REQUIRED") {
          // The temporary password was ACCEPTED — this is a first sign-in, not
          // a failure. NewPasswordForm's copy reflects that (AL-470).
          setChallengeSession(challengeResult.session);
          setChallengeUsername(username);
          setPassword("");
          setShowNewPasswordForm(true);
          setIsLoggingIn(false);
          return;
        }
        setError(
          "This account needs an extra sign-in step we do not yet support. Please contact support.",
        );
        setIsLoggingIn(false);
        return;
      }

      // Authenticate WebSocket with JWT
      {
        const idToken = getToken("idToken");

        if (idToken) {
          try {
            const authResponse = await connectWebSocket(idToken);
            if (authResponse && updateUserFromAuthResponse) {
              updateUserFromAuthResponse(authResponse);
            }
          } catch {
            // Continue to dashboard even if WebSocket auth fails
          }
        }
      }

      setUsername("");
      setPassword("");
      setError("");
      setIsLoggingIn(false);
    } catch (err: unknown) {
      clearTokens();
      setError(messageForError(err, "signIn"));
      setIsLoggingIn(false);
    }
  };

  const handleSetNewPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setIsLoggingIn(true);

    try {
      if (pendingNewPassword !== pendingConfirmPassword) {
        setError("Passwords do not match");
        setIsLoggingIn(false);
        return;
      }

      // Checked against the same policy the pool enforces, so a password
      // Cognito would reject never leaves the browser (AL-470).
      if (!newPasswordCheck.isValid) {
        setError(describePasswordPolicy(config.auth));
        setIsLoggingIn(false);
        return;
      }

      // AL-470: the provider commits the session, so isAuthenticated flips and
      // the redirect effect takes the user to the dashboard. Previously this
      // wrote tokens to storage only, leaving the provider unauthenticated and
      // ProtectedRoute bouncing the user back to /login until a page reload.
      await completeNewPassword({
        username: challengeUsername,
        session: challengeSession,
        newPassword: pendingNewPassword,
      });

      setUsername("");
      setPassword("");
      setPendingNewPassword("");
      setPendingConfirmPassword("");
      setShowNewPasswordForm(false);
      setChallengeSession("");
      setChallengeUsername("");
      setError("");
      setIsLoggingIn(false);
    } catch (err: unknown) {
      clearTokens();
      setError(messageForError(err, "newPassword"));
      setIsLoggingIn(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!resetEmail.trim()) {
      setResetMessageTone("error");
      setResetMessage("Please enter your username");
      return;
    }

    /**
     * AL-472: the reset request must look identical for an account that exists
     * and one that does not.
     *
     * Previously an unknown username produced an error and a known one produced
     * "Verification code sent to your email" — so this form answered the
     * existence question more cheaply than the login form did, with no
     * credential needed at all.
     *
     * Anything that could only be known about a real account is absorbed and
     * the neutral confirmation is shown instead. Rate limits and genuine
     * service failures still surface: neither is a statement about the account.
     */
    const showCodeStep = () => {
      setResetMessageTone("info");
      setResetMessage(RESET_CODE_REQUESTED_MESSAGE);
      setResetStep("code");
    };

    try {
      await initiatePasswordReset(resetEmail);
      showCodeStep();
    } catch (err: unknown) {
      if (revealsAccountExistence(err)) {
        showCodeStep();
        return;
      }
      setResetMessageTone("error");
      setResetMessage(messageForError(err, "resetRequest"));
    }
  };

  const handleConfirmForgotPassword = async () => {
    if (!resetCode.trim() || !newPassword.trim()) {
      setResetMessageTone("error");
      setResetMessage("Please enter the code and a new password");
      return;
    }

    // AL-470: the reset flow previously ignored both the policy check and the
    // confirm field, so mismatches and policy failures went to Cognito and came
    // back as raw provider text.
    if (newPassword !== pendingConfirmPassword) {
      setResetMessageTone("error");
      setResetMessage("Passwords do not match");
      return;
    }

    if (!resetPasswordCheck.isValid) {
      setResetMessageTone("error");
      setResetMessage(describePasswordPolicy(config.auth));
      return;
    }

    try {
      await confirmPasswordReset(resetEmail, resetCode, newPassword);
      setResetMessageTone("success");
      setResetMessage(
        "Password reset successful. Please sign in with your new password.",
      );
      setResetStep("email");
      setShowResetForm(false);
      setResetEmail("");
      setResetCode("");
      setNewPassword("");
      setPendingConfirmPassword("");
    } catch (err: unknown) {
      // AL-472: an unknown username reaching this step reads exactly like a
      // wrong code, which is also what a user who mistyped their username on
      // the previous step will now see.
      setResetMessageTone("error");
      setResetMessage(messageForError(err, "resetConfirm"));
    }
  };

  // --------------- Render ---------------

  if (isAuthLoading) {
    return (
      <div className="chat-container">
        <div className="loading">
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="chat-container">
        <div className="auth-container">
          <div className="auth-card">
            {/* Left Side - Logo Section */}
            <div className="auth-logo-section">
              <img
                src="/arcaml_logo_white_transparent.png"
                alt="Logo"
                className="auth-logo-image"
              />
              <h2 className="auth-logo-text">{config.appName}</h2>
              <p className="auth-logo-subtitle">
                {config.messages.loginHeadline}
              </p>
            </div>

            {/* Right Side - Form Section */}
            <div className="auth-form-section">
              {showNewPasswordForm ? (
                <NewPasswordForm
                  pendingNewPassword={pendingNewPassword}
                  setPendingNewPassword={setPendingNewPassword}
                  pendingConfirmPassword={pendingConfirmPassword}
                  setPendingConfirmPassword={setPendingConfirmPassword}
                  requirements={newPasswordCheck.requirements}
                  isPasswordValid={newPasswordCheck.isValid}
                  isSubmitting={isLoggingIn}
                  error={error}
                  onSubmit={handleSetNewPassword}
                  onBack={() => {
                    setShowNewPasswordForm(false);
                    setPendingNewPassword("");
                    setPendingConfirmPassword("");
                    // AL-470: clear the challenge too, not just the form.
                    setChallengeSession("");
                    setChallengeUsername("");
                    setError("");
                  }}
                />
              ) : !showResetForm ? (
                <LoginForm
                  username={username}
                  setUsername={setUsername}
                  password={password}
                  setPassword={setPassword}
                  isLoggingIn={isLoggingIn}
                  error={error}
                  config={config}
                  onSubmit={handleLogin}
                  onForgotPassword={() => setShowResetForm(true)}
                  onSignup={() => window.open(getSignupUrl(), '_blank', 'noopener,noreferrer')}
                />
              ) : (
                <ResetPasswordForm
                  resetEmail={resetEmail}
                  setResetEmail={setResetEmail}
                  resetCode={resetCode}
                  setResetCode={setResetCode}
                  newPassword={newPassword}
                  setNewPassword={setNewPassword}
                  pendingConfirmPassword={pendingConfirmPassword}
                  setPendingConfirmPassword={setPendingConfirmPassword}
                  resetStep={resetStep}
                  resetMessage={resetMessage}
                  resetMessageTone={resetMessageTone}
                  passwordRequirements={resetPasswordCheck.requirements}
                  onValidatePasswordRules={validatePasswordRules}
                  onForgotPassword={handleForgotPassword}
                  onConfirmForgotPassword={handleConfirmForgotPassword}
                  onBack={() => {
                    setShowResetForm(false);
                    setResetStep("email");
                    setResetMessage("");
                    setResetMessageTone("info");
                    setResetEmail("");
                    setResetCode("");
                    setNewPassword("");
                    setPendingConfirmPassword("");
                  }}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // If authenticated, the useEffect above will redirect to /portal/dashboard.
  return (
    <div className="chat-container">
      <div className="loading">
        <p>Redirecting to dashboard...</p>
      </div>
    </div>
  );
};

export default Auth;
